import { createContext, useContext, useEffect, useState } from 'react';
import { useAuthUser } from './AuthUserContext';
import { getCurrentUser } from 'aws-amplify/auth';
import { awsConfig } from '../config/aws.config';

type DrugCalculatorSession = {
  created_at: string;
  updated_at: string;
  type: string;
  data: {
    drug_substance_id: number;
    patient_weight?: number;
  };
  id: string;
  access_token?: string;
};

type DrugCalculatorComponentContextType = {
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  session: DrugCalculatorSession | null;
  setSession: (session: DrugCalculatorSession | null) => void;
  signature: string;
  setSignature: (signature: string) => void;
  timestamp: string;
  setTimestamp: (timestamp: string) => void;
};

const DrugCalculatorComponentContext =
  createContext<DrugCalculatorComponentContextType>({
    sessionId: '',
    setSessionId: () => {},
    session: null,
    setSession: () => {},
    signature: '',
    setSignature: () => {},
    timestamp: '',
    setTimestamp: () => {},
  });

export const DrugCalculatorComponentProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [sessionId, setSessionId] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [signature, setSignature] = useState('');
  const [session, setSession] = useState<DrugCalculatorSession | null>(null);
  const { user } = useAuthUser();

  const value = {
    sessionId,
    setSessionId,
    session,
    setSession,
    timestamp,
    setTimestamp,
    signature,
    setSignature,
  };

  const redirectToLogin = () => {
    const currentUrl = window.location.href;

    if (process.env.REACT_APP_ENTRYPOINT !== 'components') {
      window.sessionStorage.setItem('gekko/returnUrl', currentUrl);
    }

    const loginUrl =
      `https://${awsConfig.IDP_DOMAIN}/oauth2/authorize?` +
      `client_id=${awsConfig.USER_POOL_CLIENT_ID}` +
      `&response_type=code` +
      `&scope=openid%20email%20profile%20aws.cognito.signin.user.admin%20gekkovet/read%20gekkovet/write%20gekkovet/user.write%20gekkovet/user.read` +
      `&redirect_uri=${encodeURIComponent(awsConfig.REDIRECT_SIGN_IN || '')}` +
      `&state=${encodeURIComponent(currentUrl)}`;

    window.location.href = loginUrl;
  };

  const getEmbeddedTokens = () => {
    const embeddedTokensString = window.sessionStorage.getItem(
      'gekko/embeddedTokens'
    );
    if (embeddedTokensString) {
      try {
        return JSON.parse(embeddedTokensString);
      } catch (error) {
        console.error('Failed to parse embedded tokens:', error);
      }
    }
    return null;
  };

  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    console.log(
      'DrugCalculatorComponentContext: Fetching session for sessionId:',
      sessionId
    );
    setSession(null);
    fetch(`${awsConfig.COMPONENTS_API_URL}/api/v1/sessions/${sessionId}`)
      .then((response) => response.json())
      .then(async (data) => {
        setSession(data);
        if (
          signature &&
          timestamp &&
          signature !== 'dummy' &&
          timestamp !== 'dummy'
        ) {
          window.sessionStorage.setItem(
            'gekko/authheader',
            `GekkoComponents ${sessionId};${signature};${timestamp}`
          );
        }
        (window as any).__componentsCallback('sessionLoaded', data);
        console.log(
          'DrugCalculatorComponentContext: Session data received:',
          data
        );
        if (
          signature &&
          timestamp &&
          signature !== 'dummy' &&
          timestamp !== 'dummy'
        ) {
          return;
        }
        if (!data.access_token) {
          console.log(
            'DrugCalculatorComponentContext: No access_token in session, checking authentication'
          );

          if (isInIframe() && getEmbeddedTokens()) {
            console.log(
              'DrugCalculatorComponentContext: Using embedded tokens from parent window'
            );
            setSession(data);
            (window as any).__componentsCallback('sessionLoaded', data);
            return;
          }

          try {
            const cognitoUser = await getCurrentUser();
            console.log(
              'DrugCalculatorComponentContext: Cognito user found:',
              cognitoUser
            );
            setSession(data);
            (window as any).__componentsCallback('sessionLoaded', data);
          } catch (error: any) {
            if (isInIframe()) {
              console.log(
                'DrugCalculatorComponentContext: In iframe but no auth tokens, requesting from parent'
              );
              window.parent.postMessage(
                {
                  type: 'GEKKO_REQUEST_AUTH',
                  payload: { sessionId },
                },
                '*'
              );
            } else {
              console.log(
                'DrugCalculatorComponentContext: No authenticated user found, redirecting to login',
                error
              );
              redirectToLogin();
            }
          }
        } else {
          console.log(
            'DrugCalculatorComponentContext: Session has access_token, proceeding normally'
          );
          setSession(data);
          (window as any).__componentsCallback('sessionLoaded', data);
        }
      })
      .catch((error) => {
        console.error(
          'DrugCalculatorComponentContext: Error fetching session:',
          error
        );
      });
  }, [sessionId, signature, timestamp]);

  useEffect(() => {
    if (
      user.isAuthenticated &&
      process.env.REACT_APP_ENTRYPOINT !== 'components'
    ) {
      const returnUrl = window.sessionStorage.getItem('gekko/returnUrl');
      if (returnUrl && returnUrl !== window.location.href) {
        window.sessionStorage.removeItem('gekko/returnUrl');
        window.location.href = returnUrl;
      }
    }
  }, [user.isAuthenticated]);

  return (
    <DrugCalculatorComponentContext.Provider value={value}>
      {children}
    </DrugCalculatorComponentContext.Provider>
  );
};

export const useDrugCalculatorComponentContext = () => {
  return useContext(DrugCalculatorComponentContext);
};
